import type { EvaluatorResult } from "@/features/evaluator-operations/types/evaluator-registry-types";

export const CALIBRATION_RUN_SCOPE_VALUES = ["all", "category", "selected"] as const;
export type CalibrationRunScope = (typeof CALIBRATION_RUN_SCOPE_VALUES)[number];
export type CalibrationRunStatus = "queued" | "running" | "completed" | "failed";
export type CalibrationRunType = "calibration" | "regression";

export const CALIBRATION_TEST_SET_VALUES = [
  "golden",
  "safe",
  "unsafe",
  "ambiguous",
  "adversarial",
  "historical_replay",
  "human_samples",
  "synthetic",
] as const;
export type CalibrationTestSet = (typeof CALIBRATION_TEST_SET_VALUES)[number];
export const ALL_TEST_SET_CATEGORIES = "all" as const;

export const EVALUATOR_TEST_SET_STATUS_VALUES = [
  "draft",
  "approved",
  "archived",
] as const;
export type EvaluatorTestSetStatus = (typeof EVALUATOR_TEST_SET_STATUS_VALUES)[number];

export const EVALUATOR_TEST_SET_SORT_VALUES = [
  "name",
  "updatedAt",
  "createdAt",
  "latestVersion",
] as const;
export type EvaluatorTestSetSort = (typeof EVALUATOR_TEST_SET_SORT_VALUES)[number];

export const EVALUATOR_TEST_CASE_SOURCE_VALUES = [
  "manual",
  "production_trace",
  "human_review",
  "synthetic",
  "imported",
] as const;
export type EvaluatorTestCaseSource = (typeof EVALUATOR_TEST_CASE_SOURCE_VALUES)[number];

export const EVALUATOR_TEST_CASE_REVIEW_VALUES = [
  "approved",
  "needs_review",
  "unreviewed",
] as const;
export type EvaluatorTestCaseReview = (typeof EVALUATOR_TEST_CASE_REVIEW_VALUES)[number];

export type EvaluatorTestCaseSensitivity = "internal" | "protected";

export interface EvaluatorTestCaseContext {
  userRole?: string;
  region?: string;
  policyVersion?: string;
  retrievedDocuments?: readonly string[];
  conversationId?: string;
  notes?: string;
}

export interface EvaluatorTestCase {
  id: string;
  title: string;
  category: CalibrationTestSet;
  input: string;
  context?: EvaluatorTestCaseContext;
  response?: string;
  expected?: EvaluatorResult;
  source: EvaluatorTestCaseSource;
  sourceLabel: string;
  tags: readonly string[];
  sensitivity: EvaluatorTestCaseSensitivity;
  review: EvaluatorTestCaseReview;
  reviewer?: string;
  limitation?: string;
  version: number;
}

export interface CreateEvaluatorTestSetInput {
  name: string;
  description: string;
  purpose: string;
  owner: string;
  applicationScope: string;
  sensitivity: EvaluatorTestCaseSensitivity;
  categories: CalibrationTestSet[];
}

export interface CreateEvaluatorTestCaseInput {
  title: string;
  category: CalibrationTestSet;
  input: string;
  context?: EvaluatorTestCaseContext;
  response?: string;
  expected?: EvaluatorResult;
  source: EvaluatorTestCaseSource;
  sourceLabel: string;
  sensitivity: EvaluatorTestCaseSensitivity;
  limitation?: string;
}

export interface EvaluatorTestSetVersion {
  version: string;
  status: EvaluatorTestSetStatus;
  createdAt: string;
  createdBy: string;
  caseCount: number;
  approvedBy?: string;
}

export interface EvaluatorTestSet {
  id: string;
  name: string;
  description: string;
  purpose: string;
  owner: string;
  applicationScope: string;
  sensitivity: EvaluatorTestCaseSensitivity;
  categories: readonly CalibrationTestSet[];
  latestVersion: string;
  caseCount: number;
  version: number;
  versions: readonly EvaluatorTestSetVersion[];
  cases: readonly EvaluatorTestCase[];
  lastRunAt?: string;
  lastRunStatus?: "passed" | "attention" | "not_run";
}
